/**
 * Doserne Academy — Kurs catalog page.
 *
 * Shows all courses as a preview list. Cursurile sunt vizibile dar blocate
 * cu lacăt până la finalizarea verificărilor conținutului. UI: banner de
 * top explicativ + card cu icon Lock pe fiecare curs + click blocat.
 */
import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, GraduationCap, Lock, PlayCircle, Clock, Award, ShieldCheck,
  CheckCircle2, ArrowRight, Sparkles, Search, X, Eye, FileText, Bell, PenSquare,
  Pencil, Copy, Plus, Trash2, Save, Download,
} from "lucide-react";
import { useDoserneAuth } from "./doserneAuth";
import { useLang, pick } from "./i18n";
import DoserneShell from "./DoserneShell";
import LangFlag from "./LangFlag";
import { DiplomaViewer } from "./DoserneColleagues";

const LANG_NAMES_KURS = { no: "Norsk", ro: "Română", en: "English", pl: "Polski" };

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACCENT = {
  cyan:    { chip: "bg-cyan-100 text-cyan-800",       bar: "bg-cyan-500" },
  amber:   { chip: "bg-amber-100 text-amber-800",     bar: "bg-amber-500" },
  emerald: { chip: "bg-emerald-100 text-emerald-800", bar: "bg-emerald-500" },
  slate:   { chip: "bg-slate-100 text-slate-700",     bar: "bg-slate-500" },
};

const LANG_COPY = {
  no: {
    banner_title: "Kursene aktiveres snart",
    banner_body:  "Innholdet er under siste kvalitetskontroll. Så snart verifiseringen er ferdig, låses alle kurs automatisk opp for kontoen din.",
    card_locked:  "Aktiveres snart",
    click_hint:   "Kurset er låst — aktiveres snart etter verifiseringer",
    new_pill:     "Nytt",
    new_toast:    "Et nytt kurs er tilgjengelig — start HSE-introduksjonen",
    docs_title:   "Dokumenter til signering",
    doc_sign:     "Signer",
    doc_signed:   "Signert",
    doc_open:     "Åpne PDF",
    doc_download: "Last ned PDF",
    doc_notify:   "Varsle alle",
    doc_status:   "Se status",
    doc_optional: "Frivillig",
    doc_badge:    "Dokument",
    doc_meta:     "Signer med fingeren",
    doc_edit:     "Rediger",
    doc_duplicate: "Dupliser",
    doc_duplicated: "Dokument duplisert",
  },
  ro: {
    banner_title: "Cursurile se activează curând",
    banner_body:  "Conținutul este în verificare finală. Imediat ce verificările se termină, toate cursurile se vor debloca automat pentru contul tău.",
    card_locked:  "Se activează curând",
    click_hint:   "Curs blocat — se activează curând după verificări",
    new_pill:     "Nou",
    new_toast:    "Un curs nou este disponibil — începe cursul HSE de bază",
    docs_title:   "Documente de semnat",
    doc_sign:     "Semnează",
    doc_signed:   "Semnat",
    doc_open:     "Deschide PDF",
    doc_download: "Descarcă PDF",
    doc_notify:   "Anunță toți",
    doc_status:   "Vezi status",
    doc_optional: "Opțional",
    doc_badge:    "Document",
    doc_meta:     "Semnare cu degetul",
    doc_edit:     "Editează",
    doc_duplicate: "Duplică",
    doc_duplicated: "Document duplicat",
  },
  en: {
    banner_title: "Courses activating soon",
    banner_body:  "The content is going through final review. As soon as verification is complete, all courses will unlock automatically for your account.",
    card_locked:  "Unlocking soon",
    click_hint:   "Course locked — unlocking soon after verifications",
    new_pill:     "New",
    new_toast:    "A new course is available — start the HSE intro",
    docs_title:   "Documents to sign",
    doc_sign:     "Sign",
    doc_signed:   "Signed",
    doc_open:     "Open PDF",
    doc_download: "Download PDF",
    doc_notify:   "Notify all",
    doc_status:   "View status",
    doc_optional: "Optional",
    doc_badge:    "Document",
    doc_meta:     "Sign with your finger",
    doc_edit:     "Edit",
    doc_duplicate: "Duplicate",
    doc_duplicated: "Document duplicated",
  },
};
const copyFor = (lang) => LANG_COPY[lang] || LANG_COPY.no;

function CourseCard({ course, t, lang, isAdmin }) {
  const accent = ACCENT[course.accent] || ACCENT.slate;
  const c = copyFor(lang);
  const status = course.progress?.status || "not_started";
  const isPassed = status === "passed";
  const isInProgress = status === "in_progress";

  // All courses are open, unlocked sequentially by prerequisites.
  // The backend returns `locked=true` until the required previous
  // course(s) are passed. Admin can open everything.
  const canOpen = isAdmin || !course.locked;

  if (!canOpen) {
    return (
      <div
        className="relative bg-white border border-slate-200 rounded-2xl overflow-hidden h-full opacity-90 cursor-not-allowed"
        data-testid={`course-card-${course.code}`}
        onClick={() => toast.message(t("kurs.locked"))}
        role="button"
        tabIndex={0}
      >
        <div className="absolute top-3 right-3 w-8 h-8 rounded-full bg-slate-900 flex items-center justify-center" style={{ color: "#fff" }} title={t("kurs.locked")}>
          <Lock className="w-4 h-4" />
        </div>

        <div className="p-5 sm:p-6">
          <div className="text-4xl mb-3 grayscale">{course.cover_emoji || "📘"}</div>

          <div className="flex items-center gap-2 mb-2">
            <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full ${accent.chip}`}>
              {t("kurs.badge.kurs_n", { n: course.order })}
            </span>
            {course.required && (
              <span className="text-[10px] uppercase tracking-widest font-bold text-red-700 bg-red-100 px-2 py-0.5 rounded-full">
                {t("kurs.badge.mandatory")}
              </span>
            )}
          </div>

          <h3 className="text-xl font-black text-slate-900 leading-tight mb-1">{pick(course.title, lang)}</h3>
          <p className="text-sm text-slate-500 leading-relaxed line-clamp-2">{pick(course.subtitle, lang)}</p>

          <div className="flex items-center gap-4 mt-4 text-xs text-slate-500">
            <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {course.duration_min} {t("common.min")}</span>
            {course.lesson_count > 0 && (
              <span className="inline-flex items-center gap-1"><PlayCircle className="w-3.5 h-3.5" /> {t("kurs.lesson_count", { n: course.lesson_count })}</span>
            )}
            {course.quiz_count > 0 && (
              <span className="inline-flex items-center gap-1"><Award className="w-3.5 h-3.5" /> {t("kurs.quiz_count", { n: course.quiz_count })}</span>
            )}
          </div>

          <div className="mt-5 pt-4 border-t border-slate-100 flex items-center gap-2">
            <Lock className="w-3.5 h-3.5 text-slate-500" />
            <span className="text-xs font-bold uppercase tracking-wider text-slate-500" data-testid={`course-locked-label-${course.code}`}>
              {t("kurs.locked")}
            </span>
          </div>
        </div>
      </div>
    );
  }

  // Admin — clickable card, no lock icon.
  // Also: highlight the first course (order 1) since it's the one we
  // recently unlocked for everyone. Adds a soft pulsing ring + a
  // "NOU / Nytt / New" pill so workers understand this is fresh.
  const isNewForEveryone = course.order === 1 && !isAdmin && !isPassed;
  return (
    <Link
      to={`/doserne/kurs/${course.code}`}
      className={`relative bg-white border rounded-2xl overflow-hidden h-full block transition ${isNewForEveryone ? "border-emerald-400 shadow-emerald-100 shadow-lg ring-2 ring-emerald-300/60 animate-doserne-course-pulse" : "border-slate-200 hover:border-slate-300 hover:shadow-sm"}`}
      data-testid={`course-card-${course.code}`}
    >
      {isNewForEveryone && (
        <span
          className="absolute -top-2 left-4 z-10 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500 text-[10px] font-black uppercase tracking-widest shadow"
          style={{ color: "#fff" }}
          data-testid={`course-new-pill-${course.code}`}
        >
          <Sparkles className="w-3 h-3" /> {c.new_pill}
        </span>
      )}
      {isPassed && (
        <div className="absolute top-3 right-3 w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center" style={{ color: "#fff" }} title="Fullført">
          <CheckCircle2 className="w-4 h-4" />
        </div>
      )}

      <div className="p-5 sm:p-6">
        <div className="text-4xl mb-3">{course.cover_emoji || "📘"}</div>

        <div className="flex items-center gap-2 mb-2">
          <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full ${accent.chip}`}>
            {t("kurs.badge.kurs_n", { n: course.order })}
          </span>
          {course.required && (
            <span className="text-[10px] uppercase tracking-widest font-bold text-red-700 bg-red-100 px-2 py-0.5 rounded-full">
              {t("kurs.badge.mandatory")}
            </span>
          )}
        </div>

        <h3 className="text-xl font-black text-slate-900 leading-tight mb-1">{pick(course.title, lang)}</h3>
        <p className="text-sm text-slate-500 leading-relaxed line-clamp-2">{pick(course.subtitle, lang)}</p>

        <div className="flex items-center gap-4 mt-4 text-xs text-slate-500">
          <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {course.duration_min} {t("common.min")}</span>
          {course.lesson_count > 0 && (
            <span className="inline-flex items-center gap-1"><PlayCircle className="w-3.5 h-3.5" /> {t("kurs.lesson_count", { n: course.lesson_count })}</span>
          )}
          {course.quiz_count > 0 && (
            <span className="inline-flex items-center gap-1"><Award className="w-3.5 h-3.5" /> {t("kurs.quiz_count", { n: course.quiz_count })}</span>
          )}
        </div>

        {course.lesson_count > 0 && (
          <div className="mt-4">
            <div className={`h-1.5 rounded-full overflow-hidden ${isPassed ? "bg-emerald-100" : "bg-slate-100"}`}>
              <div
                className={`h-full transition-all ${isPassed ? "bg-emerald-500" : accent.bar}`}
                style={{ width: `${course.progress_percent || 0}%` }}
              />
            </div>
          </div>
        )}

        <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between">
          <span className="text-xs font-bold uppercase tracking-wider text-slate-500">
            {isPassed ? "Fullført" : isInProgress ? "Fortsett" : "Start"}
          </span>
          <ArrowRight className="w-4 h-4 text-slate-400" />
        </div>
      </div>
    </Link>
  );
}

export default function DoserneKurs() {
  const { user, loading, authHeader } = useDoserneAuth();
  const { t, lang } = useLang();
  const [courses, setCourses] = useState([]);
  const [agreements, setAgreements] = useState([]);
  const [busy, setBusy] = useState(true);
  const [showDiplomas, setShowDiplomas] = useState(false);
  const c = copyFor(lang);
  const isAdmin = !!user?.is_admin;

  const loadAgreements = async () => {
    try {
      const { data } = await axios.get(`${API}/doserne/agreements?lang=${lang}`, authHeader());
      setAgreements(data.agreements || []);
    } catch { /* non-blocking */ }
  };

  useEffect(() => {
    (async () => {
      if (!user) return;
      try {
        const { data } = await axios.get(`${API}/doserne/kurs`, authHeader());
        setCourses(data.courses || []);
      } catch { toast.error(t("common.error")); }
      await loadAgreements();
      setBusy(false);
    })();
  }, [user, authHeader, t, lang]);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#fafaf7]"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;
  if (!user) return <Navigate to="/doserne/logg-inn" replace />;

  return (
    <DoserneShell title={t("kurs.title")} subtitle={t("kurs.subtitle")}>
      {isAdmin && (
        <div
          className="mb-6 rounded-2xl border border-cyan-200 bg-gradient-to-br from-cyan-50 to-white p-5 flex items-start gap-4"
          data-testid="kurs-admin-preview-banner"
        >
          <div className="w-11 h-11 rounded-xl bg-cyan-500/15 flex items-center justify-center flex-shrink-0">
            <ShieldCheck className="w-6 h-6 text-cyan-600" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-cyan-700 font-black">Doserne Academy · Admin</div>
            <div className="text-base font-black text-slate-900">Kursene er åpne for alle</div>
            <p className="text-sm text-slate-600 leading-relaxed mt-1">
              Alle kurs er nå aktive for de ansatte, med sekvensiell opplåsing — hvert kurs åpnes når det forrige er bestått.
            </p>
            <Link
              to="/doserne/admin/kurs"
              className="inline-flex items-center gap-1.5 mt-3 px-3 py-1.5 rounded-lg bg-cyan-600 hover:bg-cyan-700 text-white text-xs font-black uppercase tracking-widest"
              data-testid="kurs-admin-settings-link"
            >
              <ShieldCheck className="w-3.5 h-3.5" /> Rediger bilder & utsteder
            </Link>
            <button
              type="button"
              onClick={() => setShowDiplomas(true)}
              className="inline-flex items-center gap-1.5 mt-3 ml-2 px-3 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-black uppercase tracking-widest"
              data-testid="kurs-admin-diplomas-link"
            >
              <Award className="w-3.5 h-3.5" /> Diplomer – kolleger
            </button>
          </div>
        </div>
      )}

      {showDiplomas && <AdminDiplomasModal authHeader={authHeader} lang={lang} onClose={() => setShowDiplomas(false)} />}

      {agreements.length > 0 && (
        <div className="mb-6" data-testid="agreements-section">
          <div className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-3 flex items-center gap-2">
            <FileText className="w-3.5 h-3.5" /> {LANG_COPY[lang]?.docs_title || LANG_COPY.no.docs_title}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {agreements.map((a) => (
              <AgreementCard key={a.code} a={a} lang={lang} isAdmin={isAdmin} authHeader={authHeader} onChanged={loadAgreements} />
            ))}
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-2xl p-5 mb-6 flex items-center gap-4">
        <div className="w-14 h-14 rounded-2xl bg-slate-900 flex items-center justify-center flex-shrink-0" style={{ color: "#fff" }}>
          <GraduationCap className="w-7 h-7 text-cyan-400" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{t("kurs.your_training")}</div>
          <div className="text-lg font-black text-slate-900">
            {busy ? "…" : `${courses.length} ${courses.length === 1 ? "kurs" : "kurs"}`}
          </div>
        </div>
      </div>

      {busy ? (
        <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
      ) : courses.length === 0 ? (
        <div className="bg-white border-2 border-dashed border-slate-200 rounded-2xl p-8 text-center text-sm text-slate-500">
          {t("kurs.start_here")}
        </div>
      ) : (
        <>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-3">{t("kurs.start_here")}</div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {courses.map((course) => <CourseCard key={course.code} course={course} t={t} lang={lang} isAdmin={isAdmin} />)}
          </div>
        </>
      )}
    </DoserneShell>
  );
}


// -----------------------------------------------------------------------------
// AdminDiplomasModal — admin overview of every issued diploma, grouped per
// person, with in-app viewing. Powers the "Diplomer – kolleger" subtab.
// -----------------------------------------------------------------------------
function AdminDiplomasModal({ authHeader, lang, onClose }) {
  const [certs, setCerts] = useState(null);
  const [q, setQ] = useState("");
  const [diploma, setDiploma] = useState(null);

  useEffect(() => {
    axios.get(`${API}/doserne/admin/certificates`, authHeader())
      .then(({ data }) => setCerts(data.certificates || []))
      .catch(() => setCerts([]));
  }, [authHeader]);

  const titleOf = (v) => (typeof v === "string" ? v : pick(v, lang));

  const people = React.useMemo(() => {
    const map = {};
    for (const cert of certs || []) {
      const key = (cert.user_email || cert.user_id || cert.user_name || "?").toLowerCase();
      if (!map[key]) map[key] = { key, name: cert.user_name || cert.user_email || "—", email: cert.user_email || "", certs: [] };
      map[key].certs.push(cert);
    }
    let list = Object.values(map);
    const term = q.trim().toLowerCase();
    if (term) list = list.filter((p) => p.name.toLowerCase().includes(term) || p.email.toLowerCase().includes(term));
    return list.sort((a, b) => a.name.localeCompare(b.name));
  }, [certs, q, lang]);

  const viewDiploma = async (certId, title) => {
    setDiploma("loading");
    try {
      const { data } = await axios.get(`${API}/doserne/certificates/${certId}/view`, authHeader());
      setDiploma({ title, html: data.html || "" });
    } catch {
      setDiploma(null);
      toast.error("Kunne ikke åpne diplomet.");
    }
  };

  return (
    <div className="fixed inset-0 z-[9998] bg-black/50 flex items-end sm:items-center justify-center sm:p-4" onClick={onClose} data-testid="admin-diplomas-modal">
      <div className="bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl max-h-[85vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
          <div className="font-black text-slate-900">Diplomer – kolleger</div>
          <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center" data-testid="admin-diplomas-close"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-3 border-b border-slate-100">
          <div className="relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Søk navn eller e-post..."
              className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-300 text-sm text-slate-900 focus:outline-none focus:border-indigo-500"
              data-testid="admin-diplomas-search"
            />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-3 space-y-3">
          {certs === null ? (
            <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
          ) : people.length === 0 ? (
            <div className="text-center py-10 text-sm text-slate-400 italic" data-testid="admin-diplomas-empty">Ingen diplomer ennå.</div>
          ) : (
            people.map((p) => (
              <div key={p.key} className="rounded-xl border border-slate-200 p-3" data-testid={`diploma-person-${p.key}`}>
                <div className="flex items-center gap-2.5 mb-2">
                  <div className="w-9 h-9 rounded-full bg-slate-900 flex items-center justify-center text-white text-sm font-black">{(p.name || "?").slice(0, 1).toUpperCase()}</div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-black text-slate-900 truncate">{p.name}</div>
                    <div className="text-[11px] text-slate-500">{p.certs.length} diplom(er)</div>
                  </div>
                </div>
                <div className="space-y-1.5">
                  {p.certs.map((cert) => (
                    <button
                      key={cert.id}
                      onClick={() => viewDiploma(cert.id, titleOf(cert.course_title))}
                      className="w-full flex items-center gap-2.5 p-2 rounded-lg bg-slate-50 hover:bg-indigo-50 text-left"
                      data-testid={`diploma-cert-${cert.id}`}
                    >
                      <Award className="w-4 h-4 text-indigo-600 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-bold text-slate-900 truncate">{titleOf(cert.course_title) || cert.course_code}</div>
                        <div className="text-[11px] text-slate-500">{cert.cert_number ? `${cert.cert_number} · ` : ""}{(cert.issued_at || "").slice(0, 10)}{typeof cert.score === "number" ? ` · ${Math.round(cert.score <= 1 ? cert.score * 100 : cert.score)}%` : ""}</div>
                      </div>
                      <Eye className="w-4 h-4 text-slate-400 flex-shrink-0" />
                    </button>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
      {diploma && <DiplomaViewer diploma={diploma} onClose={() => setDiploma(null)} />}
    </div>
  );
}

// -----------------------------------------------------------------------------
// AgreementCard — a signable document shown in the Kurs tab. Every member sees
// it and may choose to sign. Admin gets "Notify all" + "View status".
// -----------------------------------------------------------------------------
const API2 = `${process.env.REACT_APP_BACKEND_URL}/api`;

function AgreementCard({ a, lang, isAdmin, authHeader, onChanged }) {
  const c = copyFor(lang);
  const [showStatus, setShowStatus] = useState(false);
  const [notifying, setNotifying] = useState(false);
  const [editorTpl, setEditorTpl] = useState(null); // full template doc to edit
  const [working, setWorking] = useState("");

  const notifyAll = async () => {
    setNotifying(true);
    try {
      const { data } = await axios.post(`${API2}/doserne/admin/agreements/${a.code}/notify`, { lang }, authHeader());
      toast.success(`${c.doc_notify}: ${data.recipients}`);
    } catch { toast.error("Error"); }
    setNotifying(false);
  };

  const openEdit = async () => {
    setWorking("edit");
    try {
      const { data } = await axios.get(`${API2}/doserne/admin/agreement-templates`, authHeader());
      const tpl = (data.templates || []).find((t) => t.code === a.code);
      if (tpl) setEditorTpl(tpl);
    } catch { toast.error("Error"); }
    setWorking("");
  };

  const duplicate = async () => {
    setWorking("dup");
    try {
      const { data } = await axios.post(`${API2}/doserne/admin/agreement-templates`, { duplicate_from: a.code }, authHeader());
      toast.success(c.doc_duplicated || "Duplicat");
      if (onChanged) await onChanged();
      setEditorTpl(data.template);
    } catch { toast.error("Error"); }
    setWorking("");
  };

  return (
    <div className="relative h-full" data-testid={`agreement-card-${a.code}`}>
      <Link
        to={`/doserne/avtale/${a.code}`}
        className={`relative bg-white border rounded-2xl overflow-hidden h-full block transition ${a.signed ? "border-emerald-200 hover:shadow-sm" : "border-emerald-400 shadow-emerald-100 shadow-lg ring-2 ring-emerald-300/60"}`}
        data-testid={`agreement-open-${a.code}`}
      >
        {a.signed && (
          <div className="absolute top-3 right-3 w-8 h-8 rounded-full bg-emerald-500 flex items-center justify-center" style={{ color: "#fff" }} title={c.doc_signed}>
            <CheckCircle2 className="w-4 h-4" />
          </div>
        )}
        <div className="p-5 sm:p-6">
          <div className="text-4xl mb-3">{a.icon || "🕒"}</div>
          <div className="flex items-center gap-2 mb-2">
            <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full text-emerald-800 bg-emerald-100">
              {c.doc_badge}
            </span>
            <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full ${a.signed ? "text-emerald-700 bg-emerald-100" : "text-amber-700 bg-amber-100"}`}>
              {a.signed ? c.doc_signed : c.doc_optional}
            </span>
          </div>
          <h3 className="text-xl font-black text-slate-900 leading-tight mb-1">{a.title}</h3>
          <p className="text-sm text-slate-500 leading-relaxed line-clamp-2">{a.subtitle}</p>
          <div className="flex items-center gap-4 mt-4 text-xs text-slate-500">
            <span className="inline-flex items-center gap-1"><PenSquare className="w-3.5 h-3.5" /> {c.doc_meta}</span>
          </div>
          <div className="mt-4">
            <div className={`h-1.5 rounded-full overflow-hidden ${a.signed ? "bg-emerald-100" : "bg-slate-100"}`}>
              <div className="h-full transition-all bg-emerald-500" style={{ width: a.signed ? "100%" : "0%" }} />
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between">
            <span className="text-xs font-bold uppercase tracking-wider text-slate-500">
              {a.signed ? c.doc_signed : c.doc_sign}
            </span>
            <ArrowRight className="w-4 h-4 text-slate-400" />
          </div>
        </div>
      </Link>

      {isAdmin && (
        <div className="mt-2 flex items-center gap-2 flex-wrap">
          <button onClick={notifyAll} disabled={notifying}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold" data-testid={`agreement-notify-${a.code}`}>
            {notifying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Bell className="w-3.5 h-3.5" />} {c.doc_notify}
          </button>
          <button onClick={() => setShowStatus(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold" data-testid={`agreement-status-${a.code}`}>
            <Eye className="w-3.5 h-3.5" /> {c.doc_status}
          </button>
          <button onClick={openEdit} disabled={working === "edit"}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 text-xs font-bold" data-testid={`agreement-edit-${a.code}`}>
            {working === "edit" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Pencil className="w-3.5 h-3.5" />} {c.doc_edit}
          </button>
          <button onClick={duplicate} disabled={working === "dup"}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 text-xs font-bold" data-testid={`agreement-duplicate-${a.code}`}>
            {working === "dup" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Copy className="w-3.5 h-3.5" />} {c.doc_duplicate}
          </button>
        </div>
      )}
      {showStatus && <AgreementStatusModal code={a.code} title={a.title} lang={lang} authHeader={authHeader} onClose={() => setShowStatus(false)} />}
      {editorTpl && (
        <AgreementEditorModal
          tpl={editorTpl} lang={lang} authHeader={authHeader}
          onClose={() => setEditorTpl(null)}
          onSaved={async () => { setEditorTpl(null); if (onChanged) await onChanged(); }}
        />
      )}
    </div>
  );
}

function AgreementStatusModal({ code, title, lang = "no", authHeader, onClose }) {
  const [data, setData] = useState(null);
  const [pdfEmail, setPdfEmail] = useState("");
  const [dlEmail, setDlEmail] = useState("");
  useEffect(() => {
    axios.get(`${API2}/doserne/admin/agreements/${code}/status`, authHeader())
      .then(({ data }) => setData(data)).catch(() => setData({ members: [] }));
  }, [code, authHeader]);

  const openSignedPdf = async (email, lg = "") => {
    setPdfEmail(`${email}:${lg}`);
    try {
      const res = await axios.get(
        `${API2}/doserne/agreements/${code}/pdf?email=${encodeURIComponent(email)}${lg ? `&lang=${lg}` : ""}`,
        { ...authHeader(), responseType: "blob" }
      );
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      alert("Kunne ikke åpne signert PDF.");
    } finally {
      setPdfEmail("");
    }
  };

  const downloadSignedPdf = async (email, name) => {
    setDlEmail(email);
    try {
      const res = await axios.get(
        `${API2}/doserne/agreements/${code}/pdf?email=${encodeURIComponent(email)}`,
        { ...authHeader(), responseType: "blob" }
      );
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const safe = (name || email).replace(/[^a-z0-9]+/gi, "-").toLowerCase();
      const link = document.createElement("a");
      link.href = url;
      link.download = `doserne-${code}-${safe}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      alert("Kunne ikke laste ned signert PDF.");
    } finally {
      setDlEmail("");
    }
  };

  return (
    <div className="fixed inset-0 z-[9998] bg-black/50 flex items-end sm:items-center justify-center sm:p-4" onClick={onClose} data-testid="agreement-status-modal">
      <div className="bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl max-h-[85vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
          <div className="min-w-0">
            <div className="font-black text-slate-900 truncate">{title}</div>
            {data && <div className="text-xs text-slate-500">{data.signed_count} / {data.total}</div>}
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center"><X className="w-4 h-4" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-3 space-y-1.5">
          {data === null ? (
            <div className="py-10 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
          ) : data.members.length === 0 ? (
            <div className="text-center py-10 text-sm text-slate-400 italic">—</div>
          ) : (
            data.members.map((m) => (
              <div key={m.email} className="flex items-center gap-2.5 p-2 rounded-lg bg-slate-50" data-testid={`agreement-member-${m.email}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-black ${m.signed ? "bg-emerald-500" : "bg-slate-300"}`}>
                  {m.signed ? <CheckCircle2 className="w-4 h-4" /> : (m.name || "?").slice(0, 1).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-bold text-slate-900 truncate">{m.name}</div>
                  <div className="text-[11px] text-slate-500 truncate">{m.signed ? (m.signed_at || "").slice(0, 10) : m.email}</div>
                </div>
                {m.signed && (
                  <div className="flex items-center gap-1">
                    {(data.langs && data.langs.length ? data.langs : [m.signed_lang || lang]).map((lg) => (
                      <button key={lg} type="button" onClick={() => openSignedPdf(m.email, lg)} disabled={pdfEmail === `${m.email}:${lg}`}
                        title={`${LANG_NAMES_KURS[lg] || lg.toUpperCase()}${lg === m.signed_lang ? " ✓" : ""}`}
                        data-testid={`agreement-flag-${m.email}-${lg}`}
                        className={`p-0.5 rounded-[3px] transition-transform hover:scale-110 disabled:opacity-40 ${lg === m.signed_lang ? "ring-2 ring-emerald-500" : ""}`}>
                        {pdfEmail === `${m.email}:${lg}` ? <Loader2 className="w-4 h-4 animate-spin text-slate-400" /> : <LangFlag lang={lg} size={20} />}
                      </button>
                    ))}
                    <button type="button" onClick={() => downloadSignedPdf(m.email, m.name)} disabled={dlEmail === m.email}
                      title={LANG_COPY[lang]?.doc_download || "Last ned PDF"}
                      data-testid={`agreement-pdf-download-${m.email}`}
                      className="text-slate-400 hover:text-indigo-600 disabled:opacity-40 p-1 ml-0.5">
                      {dlEmail === m.email ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                    </button>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}


// -----------------------------------------------------------------------------
// AgreementEditorModal — admin creates/edits a signable document in 3 languages.
// -----------------------------------------------------------------------------
const EDL = {
  no: { edit: "Rediger dokument", icon: "Ikon (emoji)", period: "Krev avtaleperiode (Fra–Til)",
    active: "Synlig for alle", title: "Tittel", subtitle: "Undertittel", intro: "Innledning", consent: "Bekreftelsestekst",
    sections: "Punkter", heading: "Overskrift", body: "Tekst", addSection: "Legg til punkt", save: "Lagre", saving: "Lagrer …",
    archive: "Arkiver", archived: "Arkivert", saved: "Lagret" },
  ro: { edit: "Editează document", icon: "Iconiță (emoji)", period: "Cere perioada (De la–Până la)",
    active: "Vizibil pentru toți", title: "Titlu", subtitle: "Subtitlu", intro: "Introducere", consent: "Text de confirmare",
    sections: "Puncte", heading: "Titlu punct", body: "Text", addSection: "Adaugă punct", save: "Salvează", saving: "Se salvează …",
    archive: "Arhivează", archived: "Arhivat", saved: "Salvat" },
  en: { edit: "Edit document", icon: "Icon (emoji)", period: "Require period (From–To)",
    active: "Visible to everyone", title: "Title", subtitle: "Subtitle", intro: "Intro", consent: "Confirmation text",
    sections: "Clauses", heading: "Heading", body: "Text", addSection: "Add clause", save: "Save", saving: "Saving …",
    archive: "Archive", archived: "Archived", saved: "Saved" },
};
const EDIT_LANGS = [["no", "🇳🇴 NO"], ["ro", "🇷🇴 RO"], ["en", "🇬🇧 EN"]];

function AgreementEditorModal({ tpl, lang, authHeader, onClose, onSaved }) {
  const L = EDL[lang] || EDL.no;
  const [f, setF] = useState(() => ({
    icon: tpl.icon || "📄",
    accent: tpl.accent || "#1e7a3a",
    requires_period: !!tpl.requires_period,
    active: tpl.active !== false,
    order: tpl.order ?? 100,
    title: { ...(tpl.title || {}) },
    subtitle: { ...(tpl.subtitle || {}) },
    intro: { ...(tpl.intro || {}) },
    consent: { ...(tpl.consent || {}) },
    sections: (tpl.sections || []).map((s) => ({ h: { ...(s.h || {}) }, p: { ...(s.p || {}) } })),
  }));
  const [lng, setLng] = useState(lang || "no");
  const [saving, setSaving] = useState(false);

  const setMl = (field, val) => setF((cur) => ({ ...cur, [field]: { ...cur[field], [lng]: val } }));
  const setSec = (i, key, val) => setF((cur) => {
    const sections = cur.sections.map((s, idx) => (idx === i ? { ...s, [key]: { ...s[key], [lng]: val } } : s));
    return { ...cur, sections };
  });
  const addSection = () => setF((cur) => ({ ...cur, sections: [...cur.sections, { h: {}, p: {} }] }));
  const removeSection = (i) => setF((cur) => ({ ...cur, sections: cur.sections.filter((_, idx) => idx !== i) }));

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API2}/doserne/admin/agreement-templates/${tpl.code}`, f, authHeader());
      toast.success(L.saved);
      if (onSaved) await onSaved();
    } catch { toast.error("Error"); setSaving(false); }
  };
  const archive = async () => {
    if (!window.confirm(`${L.archive}?`)) return;
    setSaving(true);
    try {
      await axios.delete(`${API2}/doserne/admin/agreement-templates/${tpl.code}`, authHeader());
      toast.success(L.archived);
      if (onSaved) await onSaved();
    } catch { toast.error("Error"); setSaving(false); }
  };

  const val = (field) => f[field][lng] || "";

  return (
    <div className="fixed inset-0 z-[9999] bg-black/50 flex items-end sm:items-center justify-center sm:p-4" onClick={onClose} data-testid="agreement-editor-modal">
      <div className="bg-white w-full sm:max-w-2xl sm:rounded-2xl rounded-t-2xl max-h-[92vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
          <div className="font-black text-slate-900 flex items-center gap-2"><Pencil className="w-4 h-4" /> {L.edit}</div>
          <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center"><X className="w-4 h-4" /></button>
        </div>

        <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <label className="text-xs font-bold text-slate-500">{L.icon}</label>
            <input type="text" value={f.icon} onChange={(e) => setF((c) => ({ ...c, icon: e.target.value }))} maxLength={4}
              className="w-14 text-center px-2 py-1.5 rounded-lg border-2 border-slate-200 text-lg" data-testid="editor-icon" />
          </div>
          <label className="inline-flex items-center gap-2 text-xs font-bold text-slate-700 cursor-pointer">
            <input type="checkbox" checked={f.requires_period} onChange={(e) => setF((c) => ({ ...c, requires_period: e.target.checked }))} className="w-4 h-4 rounded text-emerald-600" data-testid="editor-period" />
            {L.period}
          </label>
          <label className="inline-flex items-center gap-2 text-xs font-bold text-slate-700 cursor-pointer">
            <input type="checkbox" checked={f.active} onChange={(e) => setF((c) => ({ ...c, active: e.target.checked }))} className="w-4 h-4 rounded text-emerald-600" data-testid="editor-active" />
            {L.active}
          </label>
        </div>

        <div className="px-4 pt-3 flex items-center gap-1.5">
          {EDIT_LANGS.map(([code2, label]) => (
            <button key={code2} onClick={() => setLng(code2)}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold ${lng === code2 ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-600"}`}
              data-testid={`editor-lang-${code2}`}>{label}</button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          <div className="mb-3">
            <label className="block text-xs font-bold text-slate-500 mb-1">{L.title}</label>
            <input type="text" value={val("title")} onChange={(e) => setMl("title", e.target.value)}
              className="w-full px-3 py-2 rounded-lg border-2 border-slate-200 focus:border-emerald-500 outline-none text-sm" data-testid="editor-title" />
          </div>
          <div className="mb-3">
            <label className="block text-xs font-bold text-slate-500 mb-1">{L.subtitle}</label>
            <input type="text" value={val("subtitle")} onChange={(e) => setMl("subtitle", e.target.value)}
              className="w-full px-3 py-2 rounded-lg border-2 border-slate-200 focus:border-emerald-500 outline-none text-sm" />
          </div>
          <div className="mb-3">
            <label className="block text-xs font-bold text-slate-500 mb-1">{L.intro}</label>
            <textarea value={val("intro")} onChange={(e) => setMl("intro", e.target.value)} rows={3}
              className="w-full px-3 py-2 rounded-lg border-2 border-slate-200 focus:border-emerald-500 outline-none text-sm" />
          </div>
          <div className="mb-3">
            <label className="block text-xs font-bold text-slate-500 mb-1">{L.consent}</label>
            <textarea value={val("consent")} onChange={(e) => setMl("consent", e.target.value)} rows={3}
              className="w-full px-3 py-2 rounded-lg border-2 border-slate-200 focus:border-emerald-500 outline-none text-sm" />
          </div>

          <div className="mt-4 mb-2 flex items-center justify-between">
            <span className="text-xs font-black uppercase tracking-widest text-slate-500">{L.sections}</span>
            <button onClick={addSection} className="inline-flex items-center gap-1 text-xs font-bold text-emerald-700" data-testid="editor-add-section"><Plus className="w-3.5 h-3.5" /> {L.addSection}</button>
          </div>
          {f.sections.map((s, i) => (
            <div key={i} className="mb-3 p-3 rounded-xl bg-slate-50 border border-slate-200" data-testid={`editor-section-${i}`}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-[11px] font-bold text-slate-400">#{i + 1}</span>
                <button onClick={() => removeSection(i)} className="text-red-500 hover:text-red-700"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
              <input type="text" value={s.h[lng] || ""} onChange={(e) => setSec(i, "h", e.target.value)} placeholder={L.heading}
                className="w-full mb-2 px-3 py-2 rounded-lg border-2 border-slate-200 focus:border-emerald-500 outline-none text-sm font-bold" />
              <textarea value={s.p[lng] || ""} onChange={(e) => setSec(i, "p", e.target.value)} rows={2} placeholder={L.body}
                className="w-full px-3 py-2 rounded-lg border-2 border-slate-200 focus:border-emerald-500 outline-none text-sm" />
            </div>
          ))}
        </div>

        <div className="border-t border-slate-200 p-3 flex items-center gap-2">
          <button onClick={archive} disabled={saving}
            className="inline-flex items-center gap-1.5 px-3 py-2.5 rounded-lg bg-white border border-red-200 text-red-600 hover:bg-red-50 text-sm font-bold" data-testid="editor-archive">
            <Trash2 className="w-4 h-4" /> {L.archive}
          </button>
          <button onClick={save} disabled={saving}
            className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-black disabled:opacity-60" data-testid="editor-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {saving ? L.saving : L.save}
          </button>
        </div>
      </div>
    </div>
  );
}
